/**
 * ArtifactRenderer — renders structured agent artifacts inline in chat.
 * Driven by render_type_registry config: type → ui_component mapping.
 * Supports: data_table, chart, html_page, code, markdown, screen.
 *
 * Receives { type, payload, title, component } from a message.artifact
 * field set by useChat when the SSE stream contains an "artifact" event.
 */
import React, { useState, useCallback } from 'react';
import DynamicScreenRenderer from './DynamicScreenRenderer';
import './ArtifactRenderer.css';

// ── Sub-renderers ─────────────────────────────────────────────────────────────

function DataGrid({ payload }) {
    const columns = payload?.columns || [];
    const rows    = payload?.rows    || [];
    if (!columns.length && !rows.length) {
        return <p className="artifact-empty">No data available.</p>;
    }
    return (
        <div className="artifact-table-wrap">
            <table className="artifact-table">
                <thead>
                    <tr>
                        {columns.map((col, i) => (
                            <th key={i}>{typeof col === 'object' ? (col.header || col.field || col.label || col.key || String(i)) : col}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, ri) => (
                        <tr key={ri}>
                            {columns.map((col, ci) => {
                                const key = typeof col === 'object' ? (col.field || col.key || ci) : col;
                                const val = Array.isArray(row) ? row[ci] : row[key];
                                return <td key={ci}>{val == null ? '' : String(val)}</td>;
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function ChartRenderer({ payload }) {
    // recharts is not in the dependency tree — render as an HTML table fallback.
    // The columns/rows shape mirrors DataGrid so the same payload works for both.
    const label  = payload?.x_axis || payload?.label_key  || 'label';
    const value  = payload?.y_axis || payload?.value_key  || 'value';
    const series = payload?.series || payload?.data || [];

    if (!series.length) {
        return <p className="artifact-empty">No chart data available.</p>;
    }

    // Compute max for a CSS bar-chart approximation
    const maxVal = Math.max(...series.map(d => Number(d[value] ?? 0) || 0), 1);

    return (
        <div className="artifact-chart">
            <div className="artifact-chart-bars">
                {series.map((d, i) => {
                    const pct = ((Number(d[value] ?? 0) / maxVal) * 100).toFixed(1);
                    return (
                        <div key={i} className="artifact-chart-row">
                            <span className="artifact-chart-label">{d[label] ?? i}</span>
                            <div className="artifact-chart-bar-track">
                                <div className="artifact-chart-bar" style={{ width: `${pct}%` }} />
                            </div>
                            <span className="artifact-chart-value">{d[value] ?? ''}</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function SandboxedIframe({ payload, title }) {
    return (
        <iframe
            className="artifact-iframe"
            srcDoc={payload?.html ?? ''}
            // LLM-generated HTML: scripts may run, but never with the portal's
            // origin — allow-same-origin would let them read the stored JWT.
            sandbox="allow-scripts"
            title={title || 'Artifact HTML page'}
            style={{ width: '100%', height: 400, border: '1px solid #ccc' }}
        />
    );
}

function CodeBlock({ payload }) {
    const code     = payload?.code     ?? payload?.content ?? '';
    const language = payload?.language ?? payload?.lang    ?? '';
    const [copied, setCopied] = useState(false);

    const handleCopy = useCallback(() => {
        navigator.clipboard?.writeText(code).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        });
    }, [code]);

    return (
        <div className="artifact-code-wrap">
            <div className="artifact-code-toolbar">
                {language && <span className="artifact-code-lang">{language}</span>}
                <button
                    type="button"
                    className="artifact-copy-btn"
                    onClick={handleCopy}
                    title="Copy to clipboard"
                >
                    {copied ? '✓ Copied' : 'Copy'}
                </button>
            </div>
            <pre className="artifact-pre"><code className={language ? `language-${language}` : ''}>{code}</code></pre>
        </div>
    );
}

function MarkdownRenderer({ payload }) {
    const content = payload?.content ?? payload?.markdown ?? payload?.text ?? '';
    return (
        <pre className="artifact-markdown">{content}</pre>
    );
}

// ── Action bar (shared between ArtifactRenderer and DynamicScreenRenderer) ────

export function ActionBar({ payload }) {
    const actions = payload?.actions ?? [];
    const [results, setResults] = useState({});

    const handleAction = useCallback(async (action) => {
        if (action.confirm_message) {
            // eslint-disable-next-line no-alert
            if (!window.confirm(action.confirm_message)) return;
        }
        try {
            const res = await fetch(action.api_path, {
                method:  action.method || 'POST',
                headers: { 'Content-Type': 'application/json' },
                body:    action.body ? JSON.stringify(action.body) : undefined,
            });
            const text = await res.text();
            let msg;
            try { msg = JSON.parse(text)?.message || JSON.parse(text)?.status || text; } catch { msg = text; }
            setResults(prev => ({ ...prev, [action.label]: { ok: res.ok, msg } }));
        } catch (err) {
            setResults(prev => ({ ...prev, [action.label]: { ok: false, msg: err.message } }));
        }
    }, []);

    return (
        <div className="artifact-action-bar">
            {actions.map((action, i) => (
                <span key={i} className="artifact-action-wrap">
                    <button
                        type="button"
                        className={`artifact-action-btn${action.variant === 'danger' ? ' artifact-action-btn--danger' : ''}`}
                        onClick={() => handleAction(action)}
                        title={action.description || action.label}
                    >
                        {action.label}
                    </button>
                    {results[action.label] && (
                        <span className={`artifact-action-result${results[action.label].ok ? '' : ' artifact-action-result--error'}`}>
                            {results[action.label].msg}
                        </span>
                    )}
                </span>
            ))}
        </div>
    );
}

// ── Component map ─────────────────────────────────────────────────────────────

const COMPONENT_MAP = {
    DataGrid:              (props) => <DataGrid {...props} />,
    ChartRenderer:         (props) => <ChartRenderer {...props} />,
    SandboxedIframe:       (props) => <SandboxedIframe {...props} />,
    CodeBlock:             (props) => <CodeBlock {...props} />,
    MarkdownRenderer:      (props) => <MarkdownRenderer {...props} />,
    DynamicScreenRenderer: (props) => <DynamicScreenRenderer components={props.payload?.components ?? []} />,
};

// ── ArtifactRenderer ──────────────────────────────────────────────────────────

export default function ArtifactRenderer({ type, payload, title, component }) {
    const resolvedTitle = title || type || 'Artifact';

    const renderBody = () => {
        // Prefer explicit component name from render_type_registry if available
        if (component && COMPONENT_MAP[component]) {
            return COMPONENT_MAP[component]({ payload, title });
        }

        switch (type) {
            case 'data_table':
                return <DataGrid payload={payload} />;
            case 'chart':
                return <ChartRenderer payload={payload} />;
            case 'html_page':
                return <SandboxedIframe payload={payload} title={title} />;
            case 'code':
                return <CodeBlock payload={payload} />;
            case 'markdown':
                return <MarkdownRenderer payload={payload} />;
            case 'screen':
                return <DynamicScreenRenderer components={payload?.components ?? []} />;
            default:
                return <div className="artifact-unknown">Unsupported artifact type: {type}</div>;
        }
    };

    return (
        <div className="artifact-renderer">
            <details open>
                <summary className="artifact-summary">
                    <span className="artifact-type-badge">{type}</span>
                    {resolvedTitle !== type && <span className="artifact-title">{resolvedTitle}</span>}
                </summary>
                <div className="artifact-body">
                    {renderBody()}
                </div>
            </details>
        </div>
    );
}
